import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import ImageIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ImageIconPath";
import { useTranslation } from "@coworker/locales";
import styles from "./AddOrEditComment.module.css";
import { AddImagesModal } from "../AddImagesModal/AddImagesModal";
import { useCallback, useMemo, useState } from "react";
import {
  ExistingFile,
  filesToNewFiles,
  NewOrExistingFile,
  newOrExistingFilesToFiles,
} from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import InputWrapper from "../InputWrapper/InputWrapper";
import { MAX_COMMENT_TEXT_LENGTH } from "../../types/Comment";
import { applyRotations } from "@coworker/apprestructured/src/task-management/util";

type AddOrEditCommentProps = {
  text: string;
  images: File[];
  existingImages?: ExistingFile[];
  disableAllInputs: boolean;
  disableAddButton: boolean;
  buttonText: string;
  label: string;
  onTextChange: (text: string) => void;
  onImagesChange: (images: File[]) => void;
  onExistingImagesChange?: (images: ExistingFile[]) => void;
  onAllImagesChange?: (images: NewOrExistingFile[]) => void;
  onAddClick: () => void;
};

export const AddOrEditComment = ({
  text,
  images,
  existingImages = [],
  disableAllInputs,
  disableAddButton,
  buttonText,
  label,
  onTextChange,
  onImagesChange,
  onExistingImagesChange,
  onAllImagesChange,
  onAddClick,
}: AddOrEditCommentProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState<NewOrExistingFile[]>([]);
  const [showRequiredError, setShowRequiredError] = useState(false);

  const { t } = useTranslation();

  const totalImagesCount = images.length + existingImages.length;

  const handleAddImagesButtonClick = useCallback(() => {
    setModalImages([...existingImages, ...filesToNewFiles(images)]);
    setIsModalOpen(true);
  }, [existingImages, images]);

  const handleAddImagesModalConfirmClick = useCallback(async () => {
    try {
      const processedImages = await applyRotations(modalImages);
      onAllImagesChange?.(processedImages);
      onImagesChange(newOrExistingFilesToFiles(processedImages));
      onExistingImagesChange?.(
        processedImages.filter(
          (image): image is ExistingFile => image.type === "existing"
        )
      );
    } catch (error) {
      console.error("Failed to apply image rotations", error);
    }
  }, [modalImages, onImagesChange, onExistingImagesChange, onAllImagesChange]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const textError = useMemo(() => {
    if (text.length > MAX_COMMENT_TEXT_LENGTH) {
      return t("maximumCharactersLengthString", {
        count: MAX_COMMENT_TEXT_LENGTH,
      });
    }

    if (showRequiredError && !text.trim() && totalImagesCount === 0) {
      return t("pleaseAddCommentString");
    }

    return null;
  }, [showRequiredError, text, t, totalImagesCount]);

  const handleAddCommentClick = useCallback(() => {
    if (!text.trim() && totalImagesCount === 0) {
      setShowRequiredError(true);
      return;
    }

    if (text.length <= MAX_COMMENT_TEXT_LENGTH) {
      setShowRequiredError(false);
      onAddClick();
    }
  }, [onAddClick, text, totalImagesCount]);

  return (
    <div>
      <InputWrapper error={textError}>
        <FixaTextArea
          className={styles["textArea"]}
          value={text}
          disabled={disableAllInputs}
          label={label}
          onChange={(e) => {
            const nextText = e.target.value;
            if (
              showRequiredError &&
              (nextText.trim() || totalImagesCount > 0)
            ) {
              setShowRequiredError(false);
            }
            onTextChange(nextText);
          }}
        />
      </InputWrapper>
      <div className={styles["buttonWrapper"]}>
        <FixaButton
          size="xsmall"
          text={
            totalImagesCount > 0
              ? `${t("addImagesString")} (${totalImagesCount})`
              : t("addImagesString")
          }
          type="secondary"
          disabled={disableAllInputs}
          ssrIcon={ImageIconPath}
          onClick={handleAddImagesButtonClick}
        />
        <FixaButton
          size="xsmall"
          text={buttonText}
          type="emphasised"
          disabled={disableAllInputs || disableAddButton}
          onClick={handleAddCommentClick}
        />
      </div>
      <AddImagesModal
        isOpen={isModalOpen}
        images={modalImages}
        onImagesChange={setModalImages}
        onClose={handleCloseModal}
        onConfirmClick={handleAddImagesModalConfirmClick}
      />
    </div>
  );
};
